<script setup lang="ts">
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import FileUploadCard from '@/components/ui/FileUploadCard.vue';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import AcceptedDocumentTypes from './AcceptedDocumentTypes.vue';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const isPoaUploaded = ref(false);
const previousFile = ref<File | undefined>(undefined);
const kycSessionStore = useKycSessionStore();
const { kycSession } = storeToRefs(kycSessionStore);

onMounted(async () => {
  const id = kycSession.value?.id;
  if (!id || !kycSession.value) {
    return;
  }
  await kycSession.value.refresh();
  await kycSession.value.proofOfAddress?.loadFileMetadata();
  const isExpired = kycSession.value.proofOfAddress?.isExpired();
  if (kycSession.value.proofOfAddress?.status === 'uploaded' && !isExpired) {
    handleUiUpdate(kycSession.value.proofOfAddress?.fileName);
  }
});

function handleUiUpdate(fileName: string | undefined) {
  if (!fileName) {
    return;
  }
  previousFile.value = new File([], fileName);
  isPoaUploaded.value = true;
}

async function uploadFile(file: File) {
  isPoaUploaded.value = false;
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }
  const cardholderId = kycSession.value.cardholder.accountId;
  const fileUpload = await kycSession.value.uploadFile({ file, cardholderId: cardholderId });
  await kycSession.value.attachKycSessionDocument({
    fileId: fileUpload.id,
    sessionId: kycSession.value.id,
    attachmentType: 'proof-of-address',
  });
  isPoaUploaded.value = true;
}
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col" data-test="screen-kyc-poa">
      <h1 class="mb-4 text-3xl">Upload a proof of residential address document</h1>
      <p class="text-light-secondary dark:text-dark-secondary mb-6">
        Upload an official proof of address document that clearly shows your full name, address, and
        is dated within the last 3 months. Only scans or photos of physical documents are allowed.
        Screenshots or photos of a screen will be rejected.
      </p>
      <AcceptedDocumentTypes class="mb-4" />
      <FileUploadCard :uploadFile="uploadFile" :previousFile="previousFile" />
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton text="Next" direction="forward" :disabled="!isPoaUploaded" />
    </template>
  </KycSessionLayout>
</template>
